import React, { useMemo } from 'react';
import { CircleCheckBig, Trash2, Trophy, Star } from 'lucide-react';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { Task } from '@/types/tasks';
import TaskCard from '../tasks/TaskCard';
import { Button } from '@/components/ui/button';
import GlassPill from '@/components/ui/GlassPill';
import { motion } from 'framer-motion';
import { useTaskFilter } from '@/components/project/ProjectTasksPage';

interface GoalCompletedTasksSectionProps {
  tasks: Task[];
  handleClearCompletedTasks?: (e: React.MouseEvent) => void;
  taskOperations?: {
    completeTask?: (taskId: string) => void;
    uncompleteTask?: (taskId: string) => void;
    removeTask?: (taskId: string) => void;
    updateTask?: (taskId: string, updates: Partial<Task>, silent?: boolean) => void;
  };
}

const GoalCompletedTasksSection: React.FC<GoalCompletedTasksSectionProps> = ({ 
  tasks, 
  handleClearCompletedTasks,
  taskOperations 
}) => {
  // Get filtered tasks from context if available
  const { filteredCompletedTasks } = useTaskFilter();
  
  // Use filtered tasks from context if available, otherwise use provided tasks
  const finalTasks = useMemo(() => {
    // If we have filtered tasks from parent context (ProjectTasksPage), use those
    if (filteredCompletedTasks && filteredCompletedTasks.length >= 0) {
      return filteredCompletedTasks;
    }
    
    // Fallback: Use provided tasks (for other contexts)
    return tasks;
  }, [filteredCompletedTasks, tasks]);
  
  // Set up droppable area for goal completed tasks
  const { setNodeRef } = useDroppable({
    id: 'completed-tasks',
  });

  return (
    <div className="relative p-3 md:p-6 rounded-lg overflow-hidden
      hover:shadow-lg transition-all duration-200 group">
      {/* Gradient background - matching card border radius */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/30 to-white/10 dark:from-black/40 dark:to-black/20 -z-10 rounded-lg" />
      
      {/* Top border highlight - matching card border radius */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-green-300/30 to-transparent dark:via-green-500/20 rounded-t-lg" />
      
      {/* Glass effect - matching card border radius */}
      <div className="absolute inset-0 backdrop-blur-xl backdrop-saturate-150 -z-10 rounded-lg" />
      
      <div className="flex items-center justify-between mb-3 md:mb-4">
        <div className="flex items-center gap-2">
          <CircleCheckBig className="text-green-500 dark:text-green-400" size={18} />
          <h3 className="font-medium text-base">Completed Tasks</h3>
          {finalTasks.length > 0 && (
            <GlassPill 
              type={finalTasks.length.toString()} 
              variant="green" 
              className="ml-2 text-xs h-6 w-6 flex items-center justify-center p-0"
            />
          )}
        </div>
        <div className="flex items-center gap-2">
          {finalTasks.length > 0 && handleClearCompletedTasks && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-3 text-muted-foreground bg-transparent hover:bg-green-500 hover:text-white dark:hover:bg-green-600 transition-all duration-200 rounded-full"
              onClick={handleClearCompletedTasks}
            >
              <Trash2 size={16} className="mr-1 group-hover:animate-pulse" />
              <span className="text-xs font-medium">Clear all</span>
            </Button>
          )}
        </div>
      </div>

      <div 
        ref={setNodeRef}
        className="pt-2 overflow-visible"
      >
        <SortableContext items={finalTasks.map(task => task.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2 min-w-0">
            {finalTasks.length === 0 ? (
              <EmptyCompletedTasks />
            ) : (
              finalTasks.map((task, index) => (
                <div key={task.id} className="min-w-0">
                  <TaskCard 
                    key={task.id} 
                    task={task} 
                    context="project"
                    customTaskOperations={taskOperations}
                  />
                </div>
              ))
            )}
          </div>
        </SortableContext>
      </div>
    </div>
  );
};

const EmptyCompletedTasks = () => (
  <motion.div 
    className="flex flex-col items-center justify-center py-6"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
  >
    <div className="relative mb-3">
      <div className="absolute inset-0 bg-gradient-to-r from-green-300/20 to-teal-300/20 dark:from-green-500/10 dark:to-teal-500/10 rounded-full blur-lg"></div>
      <motion.div
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        transition={{ 
          repeat: Infinity, 
          repeatType: "reverse", 
          duration: 2,
          ease: "easeInOut"
        }}
      >
        <div className="relative z-10 bg-gradient-to-br from-green-400 to-teal-400 dark:from-green-500 dark:to-teal-500 p-3 rounded-full">
          <Trophy size={32} className="h-8 w-8 text-white" />
        </div>
      </motion.div>
    </div>
    
    <h4 className="text-base font-semibold bg-clip-text text-transparent bg-gradient-to-r from-green-500 to-teal-500 dark:from-green-400 dark:to-teal-400 mb-2">
      Achievement awaits!
    </h4>
    
    <p className="text-sm text-center px-6 py-2 rounded-lg bg-white/50 dark:bg-white/5 backdrop-blur-sm border border-white/20 dark:border-white/10">
      Complete tasks to track your progress and celebrate your accomplishments
    </p>
    
    {/* Small decorative stars */}
    <div className="relative w-full h-6 mt-3">
      <motion.div 
        className="absolute left-1/4 -translate-x-1/2"
        animate={{ 
          y: [0, -5, 0],
          opacity: [0.5, 1, 0.5]
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        <Star size={12} className="text-green-400/60 dark:text-green-500/60" fill="currentColor" />
      </motion.div>
      <motion.div 
        className="absolute left-1/2 -translate-x-1/2"
        animate={{ 
          y: [0, -5, 0],
          opacity: [0.5, 1, 0.5]
        }}
        transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0.5
        }}
      >
        <Star size={16} className="text-teal-400/60 dark:text-teal-500/60" fill="currentColor" />
      </motion.div>
      <motion.div 
        className="absolute left-3/4 -translate-x-1/2"
        animate={{ 
          y: [0, -5, 0],
          opacity: [0.5, 1, 0.5]
        }}
        transition={{
          duration: 2.2,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1
        }}
      >
        <Star size={12} className="text-green-400/60 dark:text-green-500/60" fill="currentColor" />
      </motion.div>
    </div>
  </motion.div>
);

export default GoalCompletedTasksSection; 